import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import Swal from 'sweetalert2';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { TextField, Grid, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';

import { SCUIGeneralModal } from '../../../../../../../../atomic/molecules';
import { useRuleService } from '../../../../../../context/RuleServiceContext';
import { RuleTenant } from '../../../../../../helper/RuleServiceV2.const';
import { validateCriteriaTypes } from '../../../../../../helper/RuleServiceV2.helper';
import { RuleServiceCommand } from '../../../../../../../../services/command';
import { CTM } from './criteriaTypeModal.classes';

type IProps = {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    initialData?: any;
};

type ICriteriaTypeValue = {
    Name: string;
    Value: string;
};

type IFormData = {
    Name: string;
    Key: string;
    hasPredefinedValue: boolean;
    criteriaTypeValues: ICriteriaTypeValue[];
};

const defaultValues = {
    Key: '',
    Name: '',
    hasPredefinedValue: false,
    criteriaTypeValues: [],
};

export const CriteriaTypeModal: FC<IProps> = ({ isModalOpen, setIsModalOpen, initialData }) => {
    const { t } = useTranslation();
    const { tenant, setIsLoading, fetchCriteriaTypeGridData } = useRuleService();
    const { control, register, watch, getValues, setValue, reset } = useForm<IFormData>({
        defaultValues,
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'criteriaTypeValues',
    });
    const hasPredefinedValue = watch('hasPredefinedValue');

    // initialData geldiğinde formu doldur
    useEffect(() => {
        if (initialData) {
            reset({
                Key: initialData.key || '',
                Name: initialData.name || '',
                hasPredefinedValue: initialData.criteriaTypeValues?.length > 0,
                criteriaTypeValues: (initialData.criteriaTypeValues || []).map((v: any) => ({
                    id: v.id,
                    Name: v.name || '',
                    Value: v.value || '',
                })),
            });
        } else {
            reset(defaultValues);
        }
    }, [initialData, reset, isModalOpen]);

    const onSubmit = () => {
        let currentPayload = getValues();
        currentPayload.Key = currentPayload.Key?.replace(/\s+/g, '');

        let validation = validateCriteriaTypes(currentPayload);
        if (!validation.isValid) {
            Swal.fire(t('general.error'), t('general.emptyFieldControlText'), 'error');
            return;
        }
        if (currentPayload) {
            setIsLoading(true);
            const isUpdate = initialData && initialData.id;
            const requestPayload = {
                CriteriaType: {
                    Id: isUpdate ? initialData.id : '',
                    Tenant: tenant,
                    ...currentPayload,
                },
            };
            const req = isUpdate
                ? RuleServiceCommand.CreateCriteriaType(requestPayload)
                : RuleServiceCommand.CreateCriteriaType(requestPayload);
            req.then((res: any) => {
                if (res && (res.result?.statuscode === 200 || res.result?.StatusCode === 200)) {
                    Swal.fire(t('general.success'), ' ', 'success');
                    setIsModalOpen(false);
                    reset(defaultValues);
                    fetchCriteriaTypeGridData();
                } else {
                    Swal.fire(
                        t('general.error'),
                        res?.result?.error || res?.result?.message || t('general.error'),
                        'error'
                    );
                    setIsLoading(false);
                }
            })
                .catch(() => {
                    Swal.fire(t('general.error'), '', 'error');
                    setIsLoading(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        reset(defaultValues);
    };

    const handleModalConfirm = () => {
        onSubmit();
    };

    return (
        <SCUIGeneralModal
            width={'xs'}
            open={isModalOpen}
            onClose={handleModalClose}
            title={t('ruleManagement.modal.criteriaType.titleV2')}
            functionButtonOnClick={handleModalConfirm}
            buttonText={t('general.save')}
            rootClassName={CTM.modalRoot}
            paperClassName={CTM.modalPaper}
            titleClassName={CTM.modalTitle}
            closeIconButtonClassName={CTM.modalCloseButton}
            closeIconClassName={CTM.modalCloseIcon}
        >
            <form className={CTM.form}>
                <Grid className={CTM.formGrid} container sx={{ padding: '2rem', display: 'flex' }}>
                    <FormControl fullWidth>
                        <TextField
                            required
                            className={CTM.fieldKey}
                            sx={{ paddingBottom: '1rem' }}
                            label={t(`ruleManagement.fields.criteriaType.key.name`)}
                            disabled={false}
                            multiline
                            {...register('Key')}
                            fullWidth
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextField
                            required={true}
                            className={CTM.fieldName}
                            sx={{ paddingBottom: '1rem' }}
                            label={t(`ruleManagement.fields.criteriaType.name.name`)}
                            disabled={false}
                            {...register('Name')}
                            multiline
                            fullWidth
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextField
                            required
                            className={CTM.fieldTenant}
                            sx={{ paddingBottom: '1rem' }}
                            value={RuleTenant[+tenant as keyof typeof RuleTenant]}
                            label={t(`ruleManagement.fields.criteriaType.tenant.name`)}
                            disabled
                            multiline
                            fullWidth
                        />
                    </FormControl>
                    <FormControl className={CTM.radioSection} fullWidth sx={{ paddingBottom: '2rem' }}>
                        <FormLabel className={CTM.radioLabel} id="controlled-radio-buttons-group-label">
                            {t(`ruleManagement.modal.criteriaType.questionV2`)}
                        </FormLabel>
                        <Controller
                            control={control}
                            name="hasPredefinedValue"
                            render={({ field: { value, onChange } }) => (
                                <RadioGroup
                                    className={CTM.radioGroup}
                                    row
                                    aria-labelledby="controlled-radio-buttons-group-label"
                                    value={value ? 'true' : 'false'}
                                    onChange={(event) => {
                                        const isTrue = event.target.value === 'true';
                                        onChange(isTrue);
                                        if (!isTrue) {
                                            setValue('criteriaTypeValues', []);
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        className={CTM.radioApprove}
                                        value={true}
                                        control={<Radio />}
                                        label={t(`ruleManagement.modal.criteriaType.approve`)}
                                    />
                                    <FormControlLabel
                                        className={CTM.radioDecline}
                                        value={false}
                                        control={<Radio />}
                                        label={t(`ruleManagement.modal.criteriaType.decline`)}
                                    />
                                </RadioGroup>
                            )}
                        />
                    </FormControl>
                    {hasPredefinedValue && (
                        <div className={CTM.predefinedSection} style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className={CTM.addValueButtonWrap} style={{ width: 100 }}>
                                <IconButton
                                    className={CTM.addValueButton}
                                    size="small"
                                    sx={{ paddingTop: '1rem' }}
                                    onClick={() => append({ Name: '', Value: '' })}
                                >
                                    <AddIcon />
                                    {t('general.add')}
                                </IconButton>
                            </div>
                            <div className={CTM.valueRows} style={{ display: 'flex', flexDirection: 'column' }}>
                                {(fields || []).map((_: any, index: number) => (
                                    <div
                                        key={index}
                                        className={CTM.valueRow}
                                        data-value-index={index}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <FormControl fullWidth style={{ width: 150, margin: '20px' }}>
                                            <TextField
                                                className={CTM.valueNameField}
                                                sx={{ paddingBottom: '1rem', flex: 0.9 }}
                                                label={t(`ruleManagement.fields.criteriaType.child.name`)}
                                                size="medium"
                                                {...register(`criteriaTypeValues.${index}.Name`)}
                                            />
                                        </FormControl>
                                        <FormControl fullWidth style={{ width: 150, margin: '20px' }}>
                                            <TextField
                                                className={CTM.valueValueField}
                                                label={t(`ruleManagement.fields.criteriaType.child.value`)}
                                                sx={{ paddingBottom: '1rem', flex: 0.9 }}
                                                size="medium"
                                                {...register(`criteriaTypeValues.${index}.Value`)}
                                            />
                                        </FormControl>
                                        <IconButton
                                            className={CTM.removeValueButton}
                                            size="small"
                                            onClick={() => remove(index)}
                                        >
                                            <CloseIcon className={CTM.removeValueIcon} />
                                        </IconButton>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Grid>
            </form>
        </SCUIGeneralModal>
    );
};
